/**
 * The **mirror** (D-17, `TRACK-D` "Redux is a mirror, IndexedDB is the
 * record"). Nothing here is authoritative and nothing here is persisted: every
 * reducer below is dispatched *after* the IndexedDB write it describes has
 * already succeeded.
 *
 * It exists so a component can render a count, a banner and a confirmation
 * without touching IndexedDB during a render.
 */
import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import { sessionExpired } from '@saniplus/api-client';
import type { ErrorCode } from '@saniplus/contracts';

import type { FailedPunch, QueuedPunch, QueuedPunchState, QueuedPunchType } from './db';

export interface QueueItemSummary {
  readonly clientPunchUuid: string;
  /**
   * Carried into the mirror so a selector can prove whose punch it renders.
   *
   * Optional only because other features hand-build summaries in their specs;
   * `summarize()` always sets it, so it is present on every row the app puts
   * in the store. The enforcing copy is `QueuedPunch.ownerUserId`, which is
   * required — the mirror is not where a punch could be misattributed.
   */
  readonly ownerUserId?: string;
  readonly type: QueuedPunchType;
  readonly shiftId: string | null;
  /** The device clock reading shown as "Entrée enregistrée à 07:34". */
  readonly deviceRecordedAt: string;
  readonly attempts: number;
  readonly state: QueuedPunchState;
  readonly lastError: string | null;
}

export interface FailedItemSummary extends QueueItemSummary {
  readonly refusalCode: ErrorCode;
  readonly refusedAt: string;
  readonly acknowledged: boolean;
  /**
   * The refusal's own `error.details`, carried through verbatim.
   *
   * It is what makes W8 truthful about a punch the server refused *earlier*:
   * `OUTSIDE_PUNCH_WINDOW` carries the bounds it was judged against, and by
   * the time the worker reads the screen the live `/me/context` is describing
   * a different (or no) shift. Reading the bounds off the live context would
   * quietly show the wrong window. `unknown` because `error.details` is
   * `unknown` on the wire — `refusalWindow()` narrows it.
   */
  readonly details?: unknown;
}

export interface ConfirmedPunch {
  readonly clientPunchUuid: string;
  readonly type: QueuedPunchType;
  /** The **server's** time, never the device's — the reconciliation rule. */
  readonly recordedAt: string;
  /** Amendment #1: switch on this flag, never on the status code. */
  readonly idempotentReplay: boolean;
  /** When the confirmation arrived, for the transient CONFIRMED moment. */
  readonly confirmedAt: string;
}

export interface QueueState {
  readonly pending: QueueItemSummary[];
  readonly failed: FailedItemSummary[];
  readonly submitting: string[];
  readonly syncing: boolean;
  readonly lastSyncAt: string | null;
  readonly online: boolean;
  readonly lastConfirmed: ConfirmedPunch | null;
  readonly rehydrated: boolean;
}

export function summarize(record: QueuedPunch): QueueItemSummary {
  return {
    clientPunchUuid: record.clientPunchUuid,
    ownerUserId: record.ownerUserId,
    type: record.type,
    shiftId: record.shiftId ?? null,
    deviceRecordedAt: record.capturedAt?.deviceRecordedAt ?? record.queuedAt,
    attempts: record.attempts,
    state: record.state,
    lastError: record.lastError ?? null,
  };
}

export function summarizeFailed(record: FailedPunch): FailedItemSummary {
  return {
    ...summarize(record),
    refusalCode: record.refusalCode,
    refusedAt: record.refusedAt,
    acknowledged: record.acknowledgedAt !== undefined,
    ...(record.details === undefined ? {} : { details: record.details }),
  };
}

const initialState: QueueState = {
  pending: [],
  failed: [],
  submitting: [],
  syncing: false,
  lastSyncAt: null,
  online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  lastConfirmed: null,
  rehydrated: false,
};

function resetQueue(state: Draft<QueueState>): void {
  state.pending = [];
  state.failed = [];
  state.submitting = [];
  state.syncing = false;
  state.lastSyncAt = null;
  state.lastConfirmed = null;
  state.rehydrated = false;
}

const queueSlice = createSlice({
  name: 'offlineQueue',
  initialState,
  reducers: {
    queueRehydrated: (
      state,
      action: PayloadAction<{ pending: QueueItemSummary[]; failed: FailedItemSummary[] }>,
    ) => {
      state.pending = action.payload.pending;
      state.failed = action.payload.failed;
      state.submitting = [];
      state.rehydrated = true;
    },

    punchQueued: (state, action: PayloadAction<QueueItemSummary>) => {
      state.pending = [
        ...state.pending.filter((item) => item.clientPunchUuid !== action.payload.clientPunchUuid),
        action.payload,
      ];
      state.lastConfirmed = null;
    },

    punchSubmitting: (state, action: PayloadAction<string>) => {
      if (!state.submitting.includes(action.payload)) state.submitting.push(action.payload);
      state.pending = state.pending.map((item) =>
        item.clientPunchUuid === action.payload ? { ...item, state: 'sending' } : item,
      );
    },

    punchConfirmed: (state, action: PayloadAction<ConfirmedPunch>) => {
      state.pending = state.pending.filter(
        (item) => item.clientPunchUuid !== action.payload.clientPunchUuid,
      );
      state.submitting = state.submitting.filter((uuid) => uuid !== action.payload.clientPunchUuid);
      state.lastConfirmed = action.payload;
    },

    /** `accepted` or `duplicate` from a batch sync: gone from the queue, no fanfare. */
    punchSynced: (state, action: PayloadAction<string>) => {
      state.pending = state.pending.filter((item) => item.clientPunchUuid !== action.payload);
      state.submitting = state.submitting.filter((uuid) => uuid !== action.payload);
    },

    punchRefused: (state, action: PayloadAction<FailedItemSummary>) => {
      state.pending = state.pending.filter(
        (item) => item.clientPunchUuid !== action.payload.clientPunchUuid,
      );
      state.submitting = state.submitting.filter((uuid) => uuid !== action.payload.clientPunchUuid);
      state.failed = [
        ...state.failed.filter((item) => item.clientPunchUuid !== action.payload.clientPunchUuid),
        action.payload,
      ];
    },

    punchRetryScheduled: (
      state,
      action: PayloadAction<{
        clientPunchUuid: string;
        attempts: number;
        lastError: string | null;
      }>,
    ) => {
      state.submitting = state.submitting.filter((uuid) => uuid !== action.payload.clientPunchUuid);
      state.pending = state.pending.map((item) =>
        item.clientPunchUuid === action.payload.clientPunchUuid
          ? {
              ...item,
              state: 'pending',
              attempts: action.payload.attempts,
              lastError: action.payload.lastError,
            }
          : item,
      );
    },

    syncStarted: (state) => {
      state.syncing = true;
    },

    syncFinished: (state, action: PayloadAction<{ at: string | null }>) => {
      state.syncing = false;
      if (action.payload.at !== null) state.lastSyncAt = action.payload.at;
    },

    failedAcknowledged: (state, action: PayloadAction<string>) => {
      state.failed = state.failed.map((item) =>
        item.clientPunchUuid === action.payload ? { ...item, acknowledged: true } : item,
      );
    },

    onlineChanged: (state, action: PayloadAction<boolean>) => {
      state.online = action.payload;
    },

    /** The UI dismissed the "Pointage enregistré" moment. */
    confirmationCleared: (state) => {
      state.lastConfirmed = null;
    },

    /**
     * Sign-out. The mirror is emptied; **IndexedDB is not** — the rows stay
     * under their owner (`QueuedPunch.ownerUserId`) and come back when that
     * worker signs in again on this device. `rehydrated` goes back to `false`
     * so the shell knows the next session has not read the queue yet.
     */
    queueCleared: resetQueue,
  },
  // A session that ends by expiry (never signed out) must not leave one
  // worker's rows mirrored under the next one who picks up the device.
  extraReducers: (builder) => {
    builder.addCase(sessionExpired, resetQueue);
  },
});

export const {
  confirmationCleared,
  queueCleared,
  failedAcknowledged,
  onlineChanged,
  punchConfirmed,
  punchQueued,
  punchRefused,
  punchRetryScheduled,
  punchSubmitting,
  punchSynced,
  queueRehydrated,
  syncFinished,
  syncStarted,
} = queueSlice.actions;

export const queueReducer = queueSlice.reducer;
export const QUEUE_SLICE_NAME = queueSlice.name;
export { initialState as initialQueueState };
